import { Link } from "react-router-dom";
import { useMemo } from "react";
import { T } from "@/lib/theme";
import { ArrowUpRight } from "lucide-react";

/**
 * ConstellationClose
 * --------------------------------------------------------------------------
 * The closing section of the Arrival page. Replaces the previous repeated
 * orbital CTA. Visual idea: the structured orbit seen earlier on the site
 * has evolved into a wider constellation — a quieter, distributed sky map.
 *
 * Design notes:
 *  - Wide, deep-field starfield (deterministic positions, gentle twinkle).
 *  - 5 brand nodes + Erenown anchor placed asymmetrically across the field.
 *  - Thin connecting lines draw in slowly on enter and stay (no looping draw).
 *  - Labels in IBM Plex Mono caps, fading in after their node.
 *  - No spinning rings, no centered orbit composition. Slow drift only.
 *  - Designed to read calm, expansive, conclusive.
 * --------------------------------------------------------------------------
 */

const VB_W = 1600;
const VB_H = 900;

// Erenown anchor — positioned in the upper portion of the field so the
// constellation reads as a sky above the foreground copy. Slightly off-axis
// to avoid radial symmetry.
const ANCHOR = { id: "erenown", name: "Erenown", x: 880, y: 300, coords: "51.4769°N · 0.0005°W" };

// Five brand nodes distributed across the upper field. Coordinates chosen to
// avoid radial symmetry (which would re-create the orbit feeling) and to keep
// the lower half of the section clean for headline + CTA.
const NODES = [
  { id: "hubinall", name: "Hubinall", x: 220,  y: 130, mag: 1.0,  coords: "42.6629°N · 21.1655°E" },
  { id: "hangonin", name: "Hangonin", x: 540,  y:  80, mag: 0.9,  coords: "42.6629°N · 21.1655°E" },
  { id: "allinstra",name: "Allinstra",x: 1260, y: 130, mag: 1.0,  coords: "37.0000°N · 35.3213°E" },
  { id: "forealco", name: "Forealco", x: 1420, y: 320, mag: 0.85, coords: "50.8503°N · 4.3517°E"  },
  { id: "globedi",  name: "Globedi",  x: 820,  y: 200, mag: 0.95, coords: "50.8503°N · 4.3517°E"  },
];

// Constellation edges — each from-id to to-id. We draw Erenown→each brand
// (the lineage), and a couple of perimeter strands that suggest the brands
// also relate laterally — i.e. the field is alive, not radial.
const EDGES = [
  { from: "erenown",  to: "hubinall"  },
  { from: "erenown",  to: "hangonin"  },
  { from: "erenown",  to: "allinstra" },
  { from: "erenown",  to: "forealco"  },
  { from: "erenown",  to: "globedi"   },
  { from: "hubinall", to: "hangonin"  },
  { from: "hangonin", to: "allinstra" },
  { from: "allinstra",to: "forealco"  },
  { from: "forealco", to: "globedi"   },
];

// Deterministic pseudo-random — keeps star positions stable across renders.
function mulberry32(seed) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function generateStars(count, seed) {
  const r = mulberry32(seed);
  const stars = [];
  for (let i = 0; i < count; i++) {
    // Small clustering: bias every 7th star toward upper-right quadrant for
    // a hint of depth, otherwise full-field distribution.
    const cluster = i % 7 === 0;
    const x = cluster ? 900 + r() * 600 : r() * VB_W;
    const y = cluster ? 80  + r() * 380 : r() * VB_H;
    const sz = r();
    const radius = sz < 0.78 ? 0.6 : sz < 0.95 ? 1.1 : 1.7;
    stars.push({
      x, y, r: radius,
      min: 0.18 + r() * 0.18,
      max: 0.55 + r() * 0.4,
      dur: 4 + r() * 7,
      delay: r() * 6,
    });
  }
  return stars;
}

function nodeById(id) {
  if (id === ANCHOR.id) return ANCHOR;
  return NODES.find((n) => n.id === id);
}

function distance(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export default function ConstellationClose() {
  const stars = useMemo(() => generateStars(180, 20251224), []);

  return (
    <section
      data-testid="constellation-close"
      className="relative overflow-hidden border-t"
      style={{ borderColor: T.border, background: T.bg }}
    >
      {/* deep-field gradient — one quiet warmth point, one cool point */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            `radial-gradient(ellipse 70% 55% at 18% 80%, ${T.warm}10, transparent 60%),` +
            `radial-gradient(ellipse 60% 60% at 80% 18%, ${T.accent}10, transparent 65%)`,
        }}
      />

      {/* full-field constellation map (background) */}
      <div aria-hidden className="absolute inset-0 pointer-events-none constellation-drift">
        <svg
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          preserveAspectRatio="xMidYMid slice"
          className="w-full h-full"
        >
          <defs>
            <radialGradient id="cc-anchor-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%"  stopColor={T.warm} stopOpacity="0.95" />
              <stop offset="55%" stopColor={T.warm} stopOpacity="0.35" />
              <stop offset="100%" stopColor={T.warm} stopOpacity="0" />
            </radialGradient>
            <radialGradient id="cc-node-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%"  stopColor={T.accent} stopOpacity="0.85" />
              <stop offset="60%" stopColor={T.accent} stopOpacity="0.25" />
              <stop offset="100%" stopColor={T.accent} stopOpacity="0" />
            </radialGradient>
            <linearGradient id="cc-edge" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%"  stopColor={T.warm}   stopOpacity="0.55" />
              <stop offset="100%" stopColor={T.accent} stopOpacity="0.45" />
            </linearGradient>
          </defs>

          {/* starfield */}
          <g>
            {stars.map((s, i) => (
              <circle
                key={`s${i}`}
                cx={s.x}
                cy={s.y}
                r={s.r}
                fill={T.text}
                className="star-twinkle"
                style={{
                  "--star-min": s.min,
                  "--star-max": s.max,
                  "--star-dur": `${s.dur}s`,
                  "--star-delay": `${s.delay}s`,
                }}
              />
            ))}
          </g>

          {/* constellation edges — drawn slowly, then settle */}
          <g>
            {EDGES.map((e, i) => {
              const a = nodeById(e.from);
              const b = nodeById(e.to);
              const len = Math.ceil(distance(a, b)) + 4;
              const isAnchorEdge = e.from === "erenown" || e.to === "erenown";
              return (
                <line
                  key={`e${i}`}
                  x1={a.x} y1={a.y}
                  x2={b.x} y2={b.y}
                  stroke={isAnchorEdge ? "url(#cc-edge)" : T.borderSoft}
                  strokeWidth={isAnchorEdge ? 1 : 0.7}
                  strokeLinecap="round"
                  className="constellation-line"
                  style={{
                    "--dash-len": len,
                    "--draw-delay": `${0.4 + i * 0.18}s`,
                  }}
                />
              );
            })}
          </g>

          {/* brand nodes */}
          <g>
            {NODES.map((n, i) => (
              <g key={n.id}>
                {/* slow pulse halo */}
                <circle
                  cx={n.x} cy={n.y} r={14 * n.mag}
                  fill={T.accent} opacity="0.25"
                  className="node-pulse"
                  style={{ "--pulse-delay": `${i * 0.9}s` }}
                />
                {/* outer glow */}
                <circle cx={n.x} cy={n.y} r={28 * n.mag} fill="url(#cc-node-glow)" />
                {/* core */}
                <circle cx={n.x} cy={n.y} r={3.4 * n.mag} fill={T.accent} />
                {/* hairline ring */}
                <circle
                  cx={n.x} cy={n.y}
                  r={9 * n.mag}
                  fill="none"
                  stroke={T.accent}
                  strokeOpacity="0.45"
                  strokeWidth="0.7"
                />
              </g>
            ))}
          </g>

          {/* Erenown anchor — warm, slightly larger, no pulse halo (steady) */}
          <g>
            <circle cx={ANCHOR.x} cy={ANCHOR.y} r="46" fill="url(#cc-anchor-glow)" />
            <circle cx={ANCHOR.x} cy={ANCHOR.y} r="5.2" fill={T.warm} />
            <circle
              cx={ANCHOR.x} cy={ANCHOR.y} r="13"
              fill="none" stroke={T.warm} strokeOpacity="0.55" strokeWidth="0.8"
            />
            <circle
              cx={ANCHOR.x} cy={ANCHOR.y} r="22"
              fill="none" stroke={T.warm} strokeOpacity="0.22" strokeWidth="0.6"
              strokeDasharray="2 6"
            />
          </g>

          {/* Coordinate hairlines through the anchor — extremely subtle */}
          <g opacity="0.08">
            <line x1="0" x2={VB_W} y1={ANCHOR.y} y2={ANCHOR.y} stroke={T.text} />
            <line x1={ANCHOR.x} x2={ANCHOR.x} y1="0" y2={VB_H} stroke={T.text} />
          </g>

          {/* Labels in screen-stable units (no scaling weirdness) */}
          <g fontFamily="'IBM Plex Mono', monospace" fontSize="13" letterSpacing="3" fill={T.textSoft}>
            {NODES.map((n, i) => {
              // place label to the side of the node, depending on quadrant
              const right = n.x < VB_W / 2;
              const lx = right ? n.x + 18 : n.x - 18;
              const ly = n.y + 4;
              return (
                <text
                  key={`l${n.id}`}
                  x={lx} y={ly}
                  textAnchor={right ? "start" : "end"}
                  className="label-fade-in"
                  style={{ "--label-delay": `${1.4 + i * 0.22}s`, textTransform: "uppercase" }}
                  data-testid={`constellation-label-${n.id}`}
                >
                  {n.name}
                </text>
              );
            })}
            {/* Anchor label */}
            <text
              x={ANCHOR.x + 30}
              y={ANCHOR.y + 5}
              fill={T.warm}
              fontSize="13"
              letterSpacing="4"
              className="label-fade-in"
              style={{ "--label-delay": "0.9s", textTransform: "uppercase" }}
              data-testid="constellation-label-erenown"
            >
              Erenown · Anchor
            </text>
          </g>
        </svg>
      </div>

      {/* faint grid mask for atmospheric depth */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.06]"
        style={{
          backgroundImage: `radial-gradient(circle at center, ${T.text} 1px, transparent 1px)`,
          backgroundSize: "64px 64px",
          maskImage: "radial-gradient(ellipse 80% 70% at 50% 50%, black, transparent 90%)",
        }}
      />

      {/* Foreground content — anchored bottom-left for asymmetric calm */}
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pt-[42vh] pb-24 md:pt-[46vh] md:pb-32">
        <div className="grid grid-cols-12 gap-10 items-end">
          <div className="col-span-12 md:col-span-7 lg:col-span-6">
            <div className="flex items-center gap-4 font-plexmono text-[10.5px] tracking-[0.28em] uppercase" style={{ color: T.muted }}>
              <span style={{ color: T.accent }}>V</span>
              <span className="h-px w-10" style={{ background: T.border }} />
              <span>From orbit to constellation</span>
            </div>

            <h2
              className="font-fraunces font-light text-4xl md:text-6xl lg:text-[68px] leading-[1.04] tracking-[-0.01em] mt-8"
              style={{ color: T.text, fontVariationSettings: "'SOFT' 60" }}
              data-testid="constellation-headline"
            >
              What begins around a centre<br />
              becomes a <span className="italic" style={{ color: T.accent }}>field</span>
              <span style={{ color: T.warm }}>.</span>
            </h2>

            <p
              className="mt-8 max-w-xl font-intertight text-[15.5px] leading-[1.95]"
              style={{ color: T.textSoft }}
              data-testid="constellation-paragraph"
            >
              The orbit was the beginning. Held quietly over decades, it widens into a constellation —
              a field of aligned structures that keep their bearing without needing the centre in view.
              Erenown remains the anchor; the brands carry the standard outward.
            </p>

            <div className="mt-12 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                data-testid="constellation-cta-primary"
                className="group inline-flex items-center gap-3 px-7 py-4 font-plexmono text-[11px] tracking-[0.3em] uppercase"
                style={{ background: T.warm, color: T.bg }}
              >
                Carry the standard <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/orbit"
                data-testid="constellation-cta-secondary"
                className="inline-flex items-center gap-3 px-7 py-4 border font-plexmono text-[11px] tracking-[0.3em] uppercase"
                style={{ borderColor: T.borderSoft, color: T.text }}
              >
                Return to the orbit
              </Link>
            </div>
          </div>

          {/* Right column — quiet legend / coordinates strip, optional context */}
          <div className="col-span-12 md:col-span-5 lg:col-span-5 lg:col-start-8 hidden md:block">
            <div className="ml-auto max-w-xs">
              <div className="flex items-center justify-between font-plexmono text-[10px] tracking-[0.28em] uppercase" style={{ color: T.muted }}>
                <span>Field map · ERN-CON-01</span>
                <span style={{ color: T.accent }}>05 · aligned</span>
              </div>
              <div className="mt-5 h-px" style={{ background: T.border }} />
              <ul className="mt-5 grid grid-cols-1 gap-2 font-plexmono text-[10.5px] tracking-[0.22em] uppercase" style={{ color: T.textSoft }}>
                {NODES.map((n, i) => (
                  <li
                    key={n.id}
                    className="flex items-center justify-between gap-3"
                    data-testid={`constellation-legend-${n.id}`}
                  >
                    <span style={{ color: T.muted }}>0{i + 1}</span>
                    <span>{n.name}</span>
                    <span style={{ color: T.accentDim }}>
                      {n.coords}
                    </span>
                  </li>
                ))}
                <li className="flex items-center justify-between gap-3 pt-3 mt-2 border-t" style={{ borderColor: T.border }}>
                  <span style={{ color: T.muted }}>00</span>
                  <span style={{ color: T.warm }}>Erenown</span>
                  <span style={{ color: T.warmSoft }}>
                    {ANCHOR.coords}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
